import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Space,
  Switch,
  Table,
  Typography,
  Upload,
  message,
} from 'antd';
import { UploadOutlined, SendOutlined, PlusOutlined } from '@ant-design/icons';
import { FaWhatsapp } from 'react-icons/fa';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import specialWhatsAppBroadcastService from '../../services/specialWhatsAppBroadcastService';
import { uploadFile, UPLOAD_FOLDERS } from '../../services/uploadService';
import {
  CAPTION_MAX_LENGTH,
  getHistoryColumns,
  getTargetGroupColumns,
} from './commons/specialWhatsAppBroadcast.helpers';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const apiErrMsg = (err) =>
  err?.response?.data?.message || err?.message || 'Request failed';

export default function SpecialWhatsAppBroadcastPage() {
  const [broadcast, setBroadcast] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [sendingGroupUuid, setSendingGroupUuid] = useState(null);
  const [cronToggling, setCronToggling] = useState(false);

  const [caption, setCaption] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [cronEnabled, setCronEnabled] = useState(false);
  const [isNewDraft, setIsNewDraft] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await specialWhatsAppBroadcastService.list({ page: 1, limit: 20 });
      setHistory(res?.data?.data ?? []);
    } catch (err) {
      message.error(apiErrMsg(err));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadPreview = useCallback(async (uuid) => {
    if (!uuid) {
      setPreview(null);
      return;
    }
    try {
      const res = await specialWhatsAppBroadcastService.preview(uuid);
      setPreview(res?.data ?? null);
    } catch (err) {
      message.error(apiErrMsg(err));
    }
  }, []);

  const loadActive = useCallback(async () => {
    setLoading(true);
    try {
      const res = await specialWhatsAppBroadcastService.getActive();
      const active = res?.data ?? null;
      setBroadcast(active);
      setIsNewDraft(false);

      if (active) {
        setCaption(active.caption || '');
        setImagePreview(active.image_url || '');
        setCronEnabled(!!active.cron_enabled);
        await loadPreview(active.uuid);
      } else {
        setCaption('');
        setImagePreview('');
        setCronEnabled(false);
        setPreview(null);
      }
    } catch (err) {
      message.error(apiErrMsg(err));
    } finally {
      setLoading(false);
    }
  }, [loadPreview]);

  useEffect(() => {
    loadActive();
    loadHistory();
  }, [loadActive, loadHistory]);

  useEffect(() => {
    if (!imageFile) return undefined;
    const url = URL.createObjectURL(imageFile);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const handleStartNew = () => {
    setBroadcast(null);
    setPreview(null);
    setCaption('');
    setImageFile(null);
    setImagePreview('');
    setCronEnabled(false);
    setIsNewDraft(true);
  };

  const handleSave = async () => {
    const trimmedCaption = caption.trim();
    if (!trimmedCaption) {
      message.error('Caption is required.');
      return;
    }
    if (trimmedCaption.length > CAPTION_MAX_LENGTH) {
      message.error(`Caption must be at most ${CAPTION_MAX_LENGTH} characters.`);
      return;
    }

    setSaving(true);
    try {
      let imageUrl = broadcast?.image_url || '';

      if (imageFile) {
        const uploadResult = await uploadFile(imageFile, UPLOAD_FOLDERS.WHATSAPP_SPECIAL_BROADCASTS);
        if (!uploadResult.success || !uploadResult.url) {
          message.error(uploadResult.message || 'Image upload failed.');
          return;
        }
        imageUrl = uploadResult.url;
      }

      if (!imageUrl) {
        message.error('Please choose an image.');
        return;
      }

      let saved;
      if (broadcast?.uuid && !isNewDraft) {
        const res = await specialWhatsAppBroadcastService.update(broadcast.uuid, {
          image_url: imageUrl,
          caption: trimmedCaption,
          cron_enabled: cronEnabled,
        });
        saved = res?.data;
      } else {
        const res = await specialWhatsAppBroadcastService.create({
          image_url: imageUrl,
          caption: trimmedCaption,
          cron_enabled: cronEnabled,
        });
        saved = res?.data;
      }

      setBroadcast(saved);
      setIsNewDraft(false);
      setImageFile(null);
      message.success('Broadcast saved.');
      await loadPreview(saved?.uuid);
      await loadHistory();
    } catch (err) {
      message.error(apiErrMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const handleCronToggle = async (checked) => {
    if (!broadcast?.uuid || isNewDraft) {
      setCronEnabled(checked);
      return;
    }

    setCronToggling(true);
    try {
      const res = await specialWhatsAppBroadcastService.update(broadcast.uuid, {
        cron_enabled: checked,
      });
      const updated = res?.data;
      setBroadcast(updated);
      setCronEnabled(!!updated?.cron_enabled);
      message.success(checked ? 'Saturday auto-send enabled.' : 'Saturday auto-send disabled.');
      await loadHistory();
    } catch (err) {
      message.error(apiErrMsg(err));
    } finally {
      setCronToggling(false);
    }
  };

  const handleSendOne = useCallback(async (groupUuid) => {
    if (!broadcast?.uuid) return;

    setSendingGroupUuid(groupUuid);
    try {
      const res = await specialWhatsAppBroadcastService.sendOne(broadcast.uuid, groupUuid);
      setPreview(res?.data ?? null);
      setBroadcast(res?.data?.broadcast ?? broadcast);
      message.success('Sent to group.');
      await loadHistory();
    } catch (err) {
      message.error(apiErrMsg(err));
    } finally {
      setSendingGroupUuid(null);
    }
  }, [broadcast, loadHistory]);

  const handleSendAll = () => {
    if (!broadcast?.uuid) return;

    const pending = preview?.pending ?? 0;
    Modal.confirm({
      title: 'Send to all remaining groups?',
      content: `This will send the image to ${pending} WhatsApp group(s) with a 5 second delay between each.`,
      okText: 'Send all',
      onOk: async () => {
        setSendingAll(true);
        try {
          const res = await specialWhatsAppBroadcastService.sendAll(broadcast.uuid);
          setPreview(res?.data ?? null);
          setBroadcast(res?.data?.broadcast ?? broadcast);
          const sent = res?.data?.sent ?? 0;
          const failed = res?.data?.failed ?? 0;
          if (failed > 0) {
            message.warning(`Sent to ${sent} group(s), ${failed} failed.`);
          } else {
            message.success(`Sent to ${sent} group(s).`);
          }
          await loadHistory();
        } catch (err) {
          message.error(apiErrMsg(err));
        } finally {
          setSendingAll(false);
        }
      },
    });
  };

  const targetGroups = preview?.groups ?? [];
  const pendingCount = preview?.pending ?? 0;
  const displayImage = imagePreview || broadcast?.image_url || '';

  const groupColumns = getTargetGroupColumns({ onSendOne: handleSendOne, sendingGroupUuid });

  const historyColumns = getHistoryColumns();

  const canSend = broadcast?.uuid && !isNewDraft && pendingCount > 0;

  return (
    <DashboardPageWrapper>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Title level={3} className="!mb-1 flex items-center gap-2">
            <FaWhatsapp className="text-green-600" />
            Special WhatsApp Broadcast
          </Title>
          <Paragraph type="secondary" className="!mb-0">
            Send a custom image and caption to all WhatsApp groups with price list enabled.
          </Paragraph>
        </div>
        <Button icon={<PlusOutlined />} onClick={handleStartNew}>
          New broadcast
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="Compose" loading={loading}>
          <div className="mb-4">
            <Text className="text-sm font-medium">Image</Text>
            {displayImage ? (
              <div className="mt-2 mb-3">
                <img
                  src={displayImage}
                  alt=""
                  className="max-h-48 rounded border border-gray-200 object-contain"
                />
              </div>
            ) : null}
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={(file) => {
                setImageFile(file);
                return false;
              }}
            >
              <Button icon={<UploadOutlined />}>
                {displayImage ? 'Replace image' : 'Choose image'}
              </Button>
            </Upload>
          </div>

          <div className="mb-4">
            <Text className="text-sm font-medium">Caption</Text>
            <TextArea
              className="mt-2"
              rows={6}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              maxLength={CAPTION_MAX_LENGTH}
              showCount
              placeholder="Message shown below the image on WhatsApp"
            />
          </div>

          <div className="mb-4 flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
            <div>
              <Text className="font-medium">Auto-send Saturday 9:00 AM IST</Text>
              <Paragraph type="secondary" className="!mb-0 text-xs">
                Disable to skip the upcoming Saturday. Manual send remains available.
              </Paragraph>
              {broadcast?.scheduled_for ? (
                <Text type="secondary" className="text-xs">
                  Scheduled for: {new Date(broadcast.scheduled_for).toLocaleString()}
                </Text>
              ) : null}
            </div>
            <Switch
              checked={cronEnabled}
              loading={cronToggling}
              onChange={handleCronToggle}
            />
          </div>

          <Button type="primary" loading={saving} onClick={handleSave}>
            Save broadcast
          </Button>
        </Card>

        <Card title="Preview">
          {!displayImage && !caption.trim() ? (
            <Paragraph type="secondary">Upload an image and enter a caption to preview.</Paragraph>
          ) : (
            <div className="space-y-4">
              {displayImage ? (
                <img
                  src={displayImage}
                  alt=""
                  className="w-full max-h-64 rounded border border-gray-200 object-contain"
                />
              ) : null}
              <div className="rounded-lg bg-gray-50 p-4 whitespace-pre-wrap text-sm">
                {caption}
              </div>
              {preview ? (
                <Alert
                  type="info"
                  showIcon
                  message={`${preview.total ?? 0} target group(s) — ${preview.pending ?? 0} pending, ${preview.sent ?? 0} sent, ${preview.failed ?? 0} failed`}
                />
              ) : null}
            </div>
          )}
        </Card>
      </div>

      <Card
        className="mt-6"
        title="Target groups (price list enabled)"
        extra={(
          <Button
            type="primary"
            icon={<SendOutlined />}
            disabled={!canSend}
            loading={sendingAll}
            onClick={handleSendAll}
          >
            Send all remaining ({pendingCount})
          </Button>
        )}
      >
        {!broadcast?.uuid || isNewDraft ? (
          <Paragraph type="secondary">Save a broadcast to see target groups and send.</Paragraph>
        ) : (
          <Table
            rowKey="uuid"
            columns={groupColumns}
            dataSource={targetGroups}
            pagination={false}
            size="small"
          />
        )}
      </Card>

      <Card className="mt-6" title="History">
        <Table
          rowKey="uuid"
          columns={historyColumns}
          dataSource={history}
          loading={historyLoading}
          pagination={false}
          size="small"
        />
      </Card>
    </DashboardPageWrapper>
  );
}
